// @flow

import { ThemeProvider, css } from '@emotion/react'
import styled from '@emotion/styled'
import { Link, graphql, useStaticQuery, withPrefix } from 'gatsby'
import * as React from 'react'

import { useSession } from '@toggl/auth'
import { colors, media, themes } from '@toggl/style'
import {
  Badge,
  Button,
  Footer,
  Global,
  Header,
  Hire,
  IconCaretRight,
  IconDown,
  IconUp,
  Navigation,
  Plan,
  Responsive,
  Track,
  useCloseOnBlurEffect,
} from '@toggl/ui'
import { useAssets } from 'components'
import typeof HeaderAnnouncement from 'components/HeaderAnnouncement'

// import { keyframes } from '@emotion/core'
import { navigation } from '../../../data/global/layout.yml'

import { LayoutRoot } from './LayoutRoot'

declare var process: WebProcess

type Props = {
  ...React.ElementProps<typeof LayoutRoot>,
  announcement?: React.Element<HeaderAnnouncement>,
  children?: React.Node,
  background?: string,
}

export const LayoutGlobal = ({
  children,
  announcement,
  background,
  ...rest
}: Props): React.Element<typeof LayoutRoot> => {
  const [contentKey, _setContentKey] = React.useState<
    null | 'login' | 'products'
  >(null)
  const [hasTrackSession] = useSession()
  const uploads = useAssets({
    products: navigation.products.items?.map((i) => i.image),
    login: navigation.login.items?.map((i) => i.image),
  })

  const data = useStaticQuery(graphql`
    query layoutGlobalJobsQuery {
      allJobYaml(filter: { listed: { eq: true } }) {
        totalCount
      }
    }
  `)

  const openPositions = data.allJobYaml.totalCount
  const headerRef = React.useRef(null)

  const closeHeader = React.useCallback(() => {
    _setContentKey(null)
  }, [])

  useCloseOnBlurEffect(headerRef, closeHeader)

  const setContentKey = (val) => {
    if (contentKey === val) {
      return _setContentKey(null)
    }

    _setContentKey(val)
  }

  return (
    <LayoutRoot {...rest}>
      <ThemeProvider theme={themes.global}>
        <Header ref={headerRef}>
          {announcement ? announcement : null}
          <Navigation.Header
            onToggle={(isOpen) => {
              if (!isOpen) {
                setContentKey(null)
              }
            }}
            onMenuClose={() => setContentKey(null)}
            expandedMenu={
              contentKey && (
                <Contents
                  kind={contentKey}
                  uploads={uploads}
                  hasTrackSession={hasTrackSession}
                />
              )
            }
            open={!!contentKey}
            background={background}
            logo={
              <Link to={'/'}>
                <StyledLogo open={!!contentKey} />
              </Link>
            }
            logoWidth={['75px', '85px']}
            login={
              <Button.HeaderButtonWrapper
                ariaExpanded={contentKey === 'login'}
                onClick={() => setContentKey('login')}
                role="button"
                tabIndex={0}
              >
                Log in
                <Responsive.DesktopOnly>
                  {contentKey === 'login' ? (
                    <IconUp />
                  ) : (
                    <IconDown dark={!!contentKey} />
                  )}
                </Responsive.DesktopOnly>
                <Responsive.MobileOnly>
                  <IconCaretRight />
                </Responsive.MobileOnly>
              </Button.HeaderButtonWrapper>
            }
            cta={
              <Button.Primary
                onClick={() => setContentKey('products')}
                reducedDevicePadding
              >
                Try Toggl
              </Button.Primary>
            }
          >
            <Button.HeaderButtonWrapper
              ariaExpanded={contentKey === 'products'}
              onClick={() => setContentKey('products')}
              role="button"
              tabIndex={0}
            >
              Products
              <Responsive.DesktopOnly>
                {contentKey === 'products' ? (
                  <IconUp />
                ) : (
                  <IconDown dark={!!contentKey} />
                )}
              </Responsive.DesktopOnly>
              <Responsive.MobileOnly>
                <IconCaretRight />
              </Responsive.MobileOnly>
            </Button.HeaderButtonWrapper>
            <a
              href={'https://toggl.com/blog/'}
              rel="noreferrer"
              target="_blank"
            >
              Blog
            </a>
            <Link to={'/mission/'}>Our mission</Link>
            <Link to={'/jobs/'}>
              Working at Toggl
              {openPositions && <Badge.Wrapper>We're hiring</Badge.Wrapper>}
            </Link>
          </Navigation.Header>
        </Header>
        {children}
        <Footer.Wrapper
          Link={Link}
          openPositions={openPositions}
          logo={<IconGlobal />}
          products={[
            <>
              <TrackLink to={'/track/'} key="track">
                <Track />
                <span>Effortless time tracking</span>
              </TrackLink>
            </>,
            <>
              <PlanLink href={withPrefix('/plan/')} key="plan">
                <Plan />
                <span>Beautifully simple planning</span>
              </PlanLink>
            </>,
            <>
              <HireLink href={withPrefix('/hire/')} key="hire">
                <Hire />
                <span>Intelligent candidate-screening</span>
              </HireLink>
            </>,
          ]}
        />
      </ThemeProvider>
    </LayoutRoot>
  )
}

const StyledLogo = styled(Global)`
  fill: ${colors.darkPurple};

  ${media.mq[1]} {
    fill: ${({ open }) => (open ? colors.darkPurple : colors.yellow)};
  }
`

const Contents = ({ kind, uploads, hasTrackSession }) => {
  switch (kind) {
    case 'products':
      return <Products uploads={uploads} />
    case 'login':
      return <Login uploads={uploads} hasTrackSession={hasTrackSession} />
    case null:
      return null
    default:
      ;(kind: empty)
      throw new Error()
  }
}

const Products = ({ uploads }) => (
  <Navigation.MenuContent
    title={navigation.products.title}
    description={navigation.products.subtitle}
  >
    <Navigation.ProductsGroup>
      {navigation.products.items?.map((item, index) => {
        return (
          <Link key={index} to={item.url}>
            <Navigation.Product
              key={index}
              img={uploads.products[index]}
              title={item.title}
              description={item.subtitle}
              mediaWidth="284"
              mediaHeight="178"
            />
          </Link>
        )
      })}
    </Navigation.ProductsGroup>
  </Navigation.MenuContent>
)

const Login = ({ uploads, hasTrackSession }) => (
  <Navigation.MenuContent
    title={navigation.login.title}
    description={navigation.login.subtitle}
  >
    <Navigation.ProductsGroup>
      {navigation.login.items?.map((item, index) => {
        return (
          <Link
            key={index}
            to={
              item['login-link'] === 'track' && hasTrackSession
                ? process.env.GATSBY_TRACK_APP_URL
                : item.url
            }
          >
            <Navigation.Product
              img={uploads.login[index]}
              title={item.title}
              description={item.subtitle}
              mediaWidth="284"
              mediaHeight="178"
            />
          </Link>
        )
      })}
    </Navigation.ProductsGroup>
  </Navigation.MenuContent>
)

const IconGlobal = () => (
  <div
    css={css`
      width: 85px;
      svg {
        width: 100%;
      }
    `}
  >
    <Global />
  </div>
)

const TrackLink = styled(Link)`
  &:hover {
    color: ${colors.pink};
  }
`
const PlanLink = styled.a`
  &:hover {
    color: ${colors.violet};
  }
`
const HireLink = styled.a`
  &:hover span {
    color: ${colors.orange};
  }
`
